<template>
  <div>
    <validation-observer
      v-if="withFilters && loaded"
      ref="messages-filters-form"
      tag="form"
      class="mb-3 search-form search-form-messages">

      <f-button
        v-if="filtersBtnVisible"
        type="button"
        theme="dark"
        class="block full-width mb-3"
        @click.prevent="toggleFilters">
        <template v-if="filtersOpen">
          {{ $t('advisersFinder.collapseFilters')}}
        </template>
        <template v-else>
          {{ $t('advisersFinder.expandFilters')}}
        </template>
      </f-button>

      <transition name="slide">
        <div v-if="!filtersBtnVisible || filtersOpen">

          <div class="flex search-form-top">
            <div class="search-form-inputs flex full-width">
              <f-input-wrapper
                prependIcon="search"
                class="search  mb-1-ms">
                <f-input
                  :placeholder="$t('ui.search')"
                  v-model="filters.search"/>
              </f-input-wrapper>

              <v-select
                v-if="userType === 'adviser'"
                class="ml-1 mb-0 select select-adviser mb-1-ms ml-0-ms"
                :disabled="!allCustomers.length"
                :options="allCustomers.filter(item => item.customer_id > 0)"
                v-model="filters.customer_id"
                :reduce="(customer) => customer.customer_id"
                :placeholder="$t('messagesList.chooseCustomer')"
                :searchable="true"
                label="name">
                <template slot="no-options">
                  {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
                <template slot="option" slot-scope="option">
                  {{ option.name }} {{ option.surname }}
                </template>
                <template slot="selected-option" slot-scope="option">
                  {{ option.name }} {{ option.surname }}
                </template>
              </v-select>

              <v-select
                v-if="userType === 'customer'"
                class="ml-1 mb-0 select select-customer mb-1-ms ml-0-ms"
                :disabled="!allAdvisers.length"
                :options="allAdvisers"
                v-model="filters.adviser_id"
                :reduce="(adviser) => adviser.id"
                :placeholder="$t('messagesList.chooseExpert')"
                :searchable="true"
                label="name">
                <template slot="no-options">
                  {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
                <template slot="option" slot-scope="option">
                  {{ option.name }} {{ option.surname }}
                </template>
                <template slot="selected-option" slot-scope="option">
                  {{ option.name }} {{ option.surname }}
                </template>
              </v-select>

              <v-select
                class="ml-1 mb-0 select select-inquiry ml-0-ms"
                :disabled="!allInquiries.length"
                :options="allInquiries"
                v-model="filters.inquiry_id"
                :reduce="(inquiry) => inquiry.id"
                :placeholder="$t('messagesList.chooseOffer')"
                :searchable="true"
                label="id">
                <template slot="no-options">
                  {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
                <template slot="option" slot-scope="option">
                  {{ $t('messagesList.numberOfOffer')}} {{ option.id }}
                </template>
                <template slot="selected-option" slot-scope="option">
                  {{ $t('messagesList.numberOfOffer')}} {{ option.id }}
                </template>
              </v-select>
            </div>

            <div class="search-form-buttons flex">
              <f-button
                theme="primary"
                class="ml-1 mb-1-ms ml-0-sm"
                big
                @click.prevent="validateFiltersForm">
                {{ $t('ui.search')}}
              </f-button>

              <f-button
                theme="warning"
                class="ml-1"
                big
                @click.prevent="clearFilters">
                {{ $t('advisersFinder.resetFilters')}}
              </f-button>
            </div>
          </div>
          <div class="flex flex-align-items-center hidden-from-desktop-small-only mt-2">
            <label for="order" class="mb-0">{{ $t('advisersFinder.sort') }}:</label>
            <v-select
              class="minimal ml-1 mobile-dropdown-align-left"
              name="order"
              :options="orderOptions"
              v-model="order"
              :reduce="(item) => { return { column: item.value, direction: item.direction } }"
              :placeholder="$t('user.selectOrder')"
              :clearable="false"
              :searchable="false"
              label="label">
            </v-select>
          </div>
        </div>
      </transition>

    </validation-observer>

    <f-list
      ref="messages-list"
      :class="{
        'messages-list': true,
        'is-loading': !loaded
      }"
      :loaded="loaded"
      :listSettings="messagesListSettings"
      :listItems="messagesFormatted"
      :itemsSelectable="true"
      :bulkActions="messagesBulkActions"
      :itemRoute="messagesRoute + '/:id'"
      :data-mobile-actions-count="1" />

    <pagination
      v-if="hasPagination"
      :meta-data="paginationMetaData"
      :elementsCounterLabel="'messages'" />

    <div
      v-if="!loaded && !loadError"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
          {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>
    <f-alert
      v-if="loadError"
      theme="warning"
      visible
      class="my-4">
      <span>
        {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
      </span>
    </f-alert>
  </div>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash.debounce';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import MessagesUtils from '@/utils/MessagesUtils.js';
import Pagination from '@/components/core/Pagination.vue';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';

export default {
  name: 'messages-list',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  props: {
    withFilters: {
      type: Boolean
    },
    hasPagination: {
      type: Boolean,
      default: true
    },
    allCustomers: {
      type: Array,
      default: () => []
    },
    allInquiries: {
      type: Array,
      default: () => []
    },
    allAdvisers: {
      type: Array,
      default: () => []
    },
    customerID: {
      type: Number,
      default: 0
    },
    requestWhere: {
      type: Object
    },
    routePath: {
      type: String
    }
  },
  computed: {
    messagesFormatted () {
      return this.messagesTopics.map(item => {
        item.actionsList = ['open'];

        if (item.archived_by_adviser_at || item.archived_by_customer_at) {
          item.itemDimmed = true;
        } else {
          item.actionsList.push('archive');
        }

        item.last_message = {
          text: item.last_message,
          counter: item.messages_count,
          read: !item.new_messages_count
        }

        if (item.new_messages_count) {
          item.itemHighlited = true;
        }

        item.topicOrigin = {
          status: 'light',
          text: this.$t('messagesList.directMessage')
        }

        if (item.inquiry_id) {
          let isDisabled = !this.allInquiries.find(inquiry => inquiry.id === item.inquiry_id);
          item.topicOrigin = {
            status: 'dark',
            text: this.$t('messagesList.numberOfOffer') + item.inquiry_id,
            disabled: isDisabled,
            title: isDisabled ? this.$t('messagesList.thisOfferHasBeenRemoved') : ''
          }
        }

        return item;
      });
    },
    messagesBulkActions () {
      return [{
        label: this.$t('messagesList.archiveSelectThreads'),
        callbackFunction: this.archiveSelected,
        theme: 'danger'
      }];
    },
    messagesRoute () {
      let route = this.$route.path;
      let routeArray = route.split('/');

      if (routeArray.indexOf('lista-ofert') > -1) {
        routeArray.splice(routeArray.indexOf('lista-ofert'), 1);
      } else if (route.indexOf('pulpit') > -1) {
        routeArray.splice(routeArray.indexOf('pulpit'), 1);
      }

      if (route.indexOf('wiadomosci') === -1 && route.indexOf('lista-wiadomosci') === -1) {
        routeArray.push(this.routePath);
      }

      routeArray = routeArray.filter(item => !!item);
      return '/' + routeArray.join('/');
    },
    messagesListSettings () {
      let settingList = [
        {
          title: 'ID',
          sortable: true,
          variableName: ['id'],
          type: 'String',
          sortingEvent: 'topic-sort-toggle'
        },
        {
          title: this.$t('messagesList.originMessage'),
          variableName: ['topicOrigin'],
          type: 'f-badge',
          route: '/oferty-kredytowe/:id'
        },
      ]

      if (this.userType === 'customer') {
        settingList.push({
          title: this.$t('user.fullName'),
          variableName: ['adviser_name', 'adviser_surname'],
          type: 'String'
        });
      } else {
        settingList.push({
          title: this.$t('user.fullName'),
          variableName: ['customer_name', 'customer_surname'],
          type: 'String'
        });
      }

      settingList.push({
        mobileTitle: this.$t('messagesList.messageText'),
        title: '',
        variableName: ['last_message'],
        type: 'text-with-counter'
      });

      settingList.push({
        title: this.$t('messagesList.lastMessage'),
        variableName: ['last_message_at'],
        type: 'f-time-elapsed',
        maxTime: 0
      });

      settingList.push({
        title: '',
        variableName: ['actionsList'],
        type: 'actions',
        items: [
          {
            type: 'button',
            disabled: false,
            event: 'topic-open',
            label: this.$t('ui.open'),
            name: 'open'
          },
          {
            type: 'button',
            disabled: false,
            event: 'topic-archive',
            label: this.$t('ui.archive'),
            theme: 'danger',
            name: 'archive'
          }
        ]
      });

      return settingList;
    }
  },
  data() {
    return {
      userType: null,
      messagesTopics: [],
      loaded: false,
      loadError: false,
      archiveInProgress: false,
      filters: {
        search: '',
        adviser_id: 0,
        customer_id: 0,
        inquiry_id: 0
      },
      order: {
        column: 'last_message_at',
        direction: 'DESC'
      },
      filtersOpen: false,
      filtersBtnVisible: false,
      orderOptions: [
        {
          label: this.$t('list.fromLatest'),
          value: 'id',
          direction: 'DESC'
        },
        {
          label: this.$t('list.fromOldest'),
          value: 'id',
          direction: 'ASC'
        },
        {
          label: this.$t('messagesList.afterLastDateMessage'),
          value: 'last_message_at',
          direction: 'DESC'
        }
      ]
    };
  },
  mounted () {
    this.userType = this.$store.getters['getUserType'].toLowerCase();
    this.$bus.$on('topic-open', this.openTopic);
    this.$bus.$on('topic-archive', this.archiveTopic);
    this.$bus.$on('topic-sort-toggle', this.toggleSort);
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let endpointParams = {};

      if (this.withFilters) {
        let where = {};

        if (this.filters.search) {
          where.search = this.filters.search;
        }

        if (this.filters.adviser_id) {
          where.adviser_id = this.filters.adviser_id;
        }

        if (this.filters.customer_id) {
          where.customer_id = this.filters.customer_id;
        }

        if (this.customerID) {
          where.customer_id = this.customerID;
        }

        if (this.filters.inquiry_id) {
          where.inquiry_id = this.filters.inquiry_id;
        }

        endpointParams.where = where;
        endpointParams.order = this.order;
      }

      if (this.requestWhere) {
        endpointParams.where = this.requestWhere;
      }

      let enpointUrl = '/api/messages/topics/items';

      if (this.hasPagination) {
        enpointUrl += '?page=' + this.currentPage;
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: enpointUrl,
        listField: 'messagesTopics',
        pagination: this.hasPagination,
        params: endpointParams,
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        adviser_id: 0,
        customer_id: 0,
        inquiry_id: 0
      };

      this.order = {
        column: 'last_message_at',
        direction: 'DESC'
      }

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['messages-filters-form'], this.debouncedLoadFilteredData);
    },
    toggleSort (param) {
      this.order.column = param.columnName[0];
      this.order.direction = this.order.direction === 'ASC' ? 'DESC' : 'ASC';
      this.loadFilteredData();
    },
    openTopic (topic) {
      this.$router.push(this.messagesRoute + '/' + topic.id);
    },
    archiveTopic (topic) {
      MessagesUtils.archiveTopic(topic, this, {
        successAction: () => {
          this.loadFilteredData();
        }
      });
    },
    archiveSelected (selectedItems) {
      this.archiveInProgress = true;
      let itemsToArchive = Object.keys(selectedItems).filter(key => selectedItems[key] === true);

      ListUtils.bulkActionItems(this, {
        endpoint: '/api/messages/topics/archive-many',
        successTitle: this.$t('messagesList.archivedThreads'),
        successText: this.$t('messagesList.archivedSelectedThreads'),
        confirmTitle: this.$t('messagesList.areYouSureArchivedSelectedThreads'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('messagesList.archivedThreadsError'),
        items: itemsToArchive,
        successAction: () => {
          this.$refs['messages-list'].deselectAllItems();
          this.selectedItems.splice(0);
          this.archiveInProgress = false;
          this.$bus.$emit('pagination-reset');
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.archiveInProgress = false;
        }
      });
    },
    toggleFilters () {
      Vue.set(this, 'filtersOpen', !this.filtersOpen);
    }
  },
  beforeDestroy () {
    this.$bus.$off('topic-open', this.openTopic);
    this.$bus.$off('topic-archive', this.archiveTopic);
    this.$bus.$off('topic-sort-toggle', this.toggleSort);
  }
};
</script>
